import { Capacitor } from '@capacitor/core';

import type { HealthDataReader } from './HealthDataReader';
import { HealthKitReader } from './HealthKitReader';
import { HealthConnectReader } from './HealthConnectReader';
import { NullReader } from './NullReader';

let singleton: HealthDataReader | null = null;

/**
 * Returns the appropriate `HealthDataReader` for the current platform.
 * Cached for the app lifetime — the reader is stateless and platform is
 * fixed at runtime.
 *
 * - iOS native  → `HealthKitReader`
 * - Android     → `HealthConnectReader` (stub, `isAvailable()` false)
 * - Web/preview → `NullReader` (`isAvailable()` false)
 */
export const getHealthReader = (): HealthDataReader => {
  if (singleton) return singleton;
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  let next: HealthDataReader;
  if (platform === 'ios' && isNative) {
    next = new HealthKitReader();
  } else if (platform === 'android' && isNative) {
    next = new HealthConnectReader();
  } else {
    next = new NullReader();
  }
  singleton = next;
  return next;
};

export type { HealthDataReader } from './HealthDataReader';
export {
  CORE_HEALTH_SCOPES,
  type DailyMetric,
  type DateRange,
  type HealthPlatform,
  type HealthScope,
  type PermissionResult,
} from './HealthDataReader';
